import { Plus, RotateCcw } from "lucide-react";
import {
  InfiniteListBase,
  RecordContextProvider,
  useListContext,
  useRecordContext,
  useTimeout,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";
import { BulkExportButton } from "@/components/admin/bulk-export-button";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SelectAllButton } from "@/components/admin/select-all-button";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { ListHeader } from "../layout/ListHeader";
import { MobileContent } from "../layout/MobileContent";
import { MobileListHeader } from "../layout/MobileListHeader";
import { InfinitePagination } from "../misc/InfinitePagination";
import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";
import { CompanyBulkDeleteButton } from "./CompanyDeleteButton";
import { CompanyEmpty } from "./CompanyEmpty";
import { companyExporter } from "./companyExporter";

export const CompanyList = () => (
  <List<Company>
    title={false}
    perPage={25}
    sort={{ field: "name", order: "ASC" }}
    exporter={companyExporter}
    actions={<CompanyListActions />}
    pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
  >
    <CompanyListLayout />
  </List>
);

const CompanyListActions = () => {
  const translate = useTranslate();
  return (
    <ListHeader
      title={translate("resources.companies.name", { smart_count: 2 })}
      searchPlaceholder={translate("resources.companies.filters.search")}
    >
      <ExportButton exporter={companyExporter} />
      <CreateButton label="resources.companies.action.new" />
    </ListHeader>
  );
};

const CompanyListLayout = () => {
  const translate = useTranslate();
  const { data, error, isPending, filterValues } = useListContext<Company>();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;
  if (error) {
    return (
      <div className="text-sm text-destructive" role="alert">
        {translate("resources.companies.list.error_loading")}
      </div>
    );
  }
  if (!data?.length && !hasFilters) return <CompanyEmpty />;
  if (!data?.length) {
    return (
      <div
        className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground"
        role="status"
      >
        {translate("resources.companies.list.no_results")}
      </div>
    );
  }

  return <CompanyTable />;
};

const CompanyTable = () => (
  <DataTable<Company>
    rowClick="show"
    className="bg-card"
    bulkActionButtons={
      <>
        <SelectAllButton />
        <BulkExportButton />
        <CompanyBulkDeleteButton />
      </>
    }
  >
    <DataTable.Col source="name">
      <CompanyNameCell />
    </DataTable.Col>
    <DataTable.Col source="address" cellClassName="text-muted-foreground" />
    <DataTable.Col
      source="invoice_email"
      label="resources.companies.list.invoice_email"
      cellClassName="text-muted-foreground break-all"
    />
    <DataTable.NumberCol source="nb_contacts" />
    <DataTable.NumberCol source="nb_sponsorings" />
  </DataTable>
);

const CompanyNameCell = () => {
  const record = useRecordContext<Company>();
  if (!record) return null;
  return (
    <span className="inline-flex items-center gap-2 font-medium">
      <CompanyAvatar width={20} height={20} />
      {record.name}
    </span>
  );
};

/** Mobile list: title and search in the header, one row per company. */
export const CompanyListMobile = () => (
  <InfiniteListBase
    perPage={25}
    sort={{ field: "name", order: "ASC" }}
    exporter={companyExporter}
    queryOptions={{
      onError: () => {
        /* the content below renders the error itself */
      },
    }}
  >
    <CompanyListLayoutMobile />
  </InfiniteListBase>
);

const CompanyListLayoutMobile = () => {
  const translate = useTranslate();
  const { isPending, data, error, filterValues } = useListContext<Company>();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!isPending && !data?.length && !hasFilters) {
    return (
      <>
        <MobileListHeader
          title={translate("resources.companies.name", { smart_count: 2 })}
        />
        <MobileContent>
          <CompanyEmpty />
        </MobileContent>
      </>
    );
  }

  return (
    <>
      <MobileListHeader
        title={translate("resources.companies.name", { smart_count: 2 })}
        searchPlaceholder={translate("resources.companies.filters.search")}
      />
      <MobileContent search>
        <Button asChild className="mb-3 w-full">
          <Link to="/companies/create">
            <Plus className="size-4" />
            {translate("resources.companies.action.new")}
          </Link>
        </Button>
        <CompanyListContentMobile />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </>
  );
};

const CompanyListContentMobile = () => {
  const translate = useTranslate();
  const {
    data: companies,
    error,
    isPending,
    refetch,
  } = useListContext<Company>();
  const oneSecondHasPassed = useTimeout(1000);

  if (isPending) {
    if (!oneSecondHasPassed) return null;
    return (
      <div className="divide-y">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex flex-row items-center gap-4 py-3">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="flex-1">
              <Skeleton className="w-32 h-5 mb-2" />
              <Skeleton className="w-48 h-4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error && !companies) {
    return (
      <div className="p-4 text-center">
        <p className="text-muted-foreground mb-4">
          {translate("resources.companies.list.error_loading")}
        </p>
        <Button onClick={() => refetch()}>
          <RotateCcw />
          {translate("crm.common.retry")}
        </Button>
      </div>
    );
  }

  if (!companies.length) {
    return (
      <p className="p-4 text-muted-foreground">
        {translate("resources.companies.list.no_results")}
      </p>
    );
  }

  return (
    <div className="divide-y">
      {companies.map((company) => (
        <RecordContextProvider key={company.id} value={company}>
          <Link
            to={`/companies/${company.id}/show`}
            className="flex flex-row gap-4 items-center py-3 active:bg-accent/50 transition-colors"
          >
            <CompanyAvatar width={40} height={40} />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{company.name}</div>
              {company.address ? (
                <div className="text-sm text-muted-foreground truncate">
                  {company.address}
                </div>
              ) : null}
            </div>
          </Link>
        </RecordContextProvider>
      ))}
    </div>
  );
};
